// src/components/CallbacksControl.js
import React, { useState, useEffect } from 'react';
import './CallbacksControl.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const CallbacksControl = () => {
  const [events, setEvents] = useState([]);
  const [settings, setSettings] = useState({
    enabled: false,
    intervalSeconds: 10,
    autoScroll: false,
    selectedEventIds: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastSaveTime, setLastSaveTime] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch events and settings on component mount
  useEffect(() => {
    fetchEvents();
    fetchSettings();
  }, []);

  // Fetch all events
  const fetchEvents = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/events`);

      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }

      const data = await response.json();

      // Add safety check for data structure
      if (!Array.isArray(data)) {
        console.error('Events data is not an array:', data);
        setEvents([]);
        setLoading(false);
        return;
      }

      // Filter events that have dancers (with safety checks)
      const eventsWithDancers = data.filter(event => {
        if (!event || typeof event !== 'object') return false;
        const dancers = event.dancers || [];
        return Array.isArray(dancers) && dancers.length > 0;
      });
      setEvents(eventsWithDancers);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
      setEvents([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Fetch callback settings from backend API
  const fetchSettings = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/callbacks-settings`);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Fetched settings from backend API:', data);

      // Map snake_case API fields to camelCase component fields
      const mappedSettings = {
        id: data.id,  // Preserve the ID
        enabled: data.enabled || false,
        autoScroll: data.auto_scroll || false,
        intervalSeconds: data.interval_seconds || 10,
        selectedEventIds: data.selected_event_ids || [],
        company: data.company
      };

      console.log('🔧 CallbacksControl - Raw API data:', data);
      console.log('🔧 CallbacksControl - Mapped settings for component:', mappedSettings);
      console.log('🔧 CallbacksControl - selectedEventIds from API:', data.selected_event_ids);
      setSettings(mappedSettings);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching settings from backend:', err);
      setError('Failed to load settings. Please try again later.');
      setLoading(false);
    }
  };

  // Save settings to backend API (separate from UI state)
  const saveSettingsToBackend = async (settingsToSave) => {
    try {
      setSaving(true);
      setError(null);

      // Map camelCase component fields to snake_case API fields
      const apiData = {
        enabled: settingsToSave.enabled,
        auto_scroll: settingsToSave.autoScroll,
        interval_seconds: settingsToSave.intervalSeconds,
        selected_event_ids: settingsToSave.selectedEventIds,
        company: _company,
        id: settings.id  // Include the ID if it exists
      };

      console.log('🔴 FRONTEND API - About to send request to:', `https://${_host}:6003/api/${_company}/callbacks-settings`);
      console.log('🔴 FRONTEND API - Request data:', apiData);

      const response = await fetch(`https://${_host}:6003/api/${_company}/callbacks-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      console.log('🔴 FRONTEND API - Response status:', response.status);
      console.log('🔴 FRONTEND API - Response ok:', response.ok);

      if (!response.ok) {
        console.log('🔴 FRONTEND API - Error response text:', await response.text());
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const savedData = await response.json();
      console.log('🔴 FRONTEND API - Response data:', savedData);

      // Don't overwrite local state - keep what user set
      // setSettings(savedData); // REMOVED - This was causing the toggle!

      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ BACKEND ERROR:', err);
      setError(`Failed to save settings: ${err.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  // Simple save function
  const saveImmediately = (newSettings) => {
    console.log('🔴 FRONTEND SAVE - saveImmediately called with:', newSettings);
    console.log('🔴 FRONTEND SAVE - autoScroll value:', newSettings.autoScroll);
    console.log('🔴 FRONTEND SAVE - selectedEventIds:', newSettings.selectedEventIds);
    setSettings(newSettings); // Update UI immediately
    saveSettingsToBackend(newSettings); // Save to backend
  };

  // Handle enabling/disabling callbacks
  const handleToggleEnabled = (enabled) => {
    const newSettings = { ...settings, enabled };
    saveImmediately(newSettings);
  };

  // Handle interval change
  const handleIntervalChange = (intervalSeconds) => {
    const newSettings = { ...settings, intervalSeconds };
    saveImmediately(newSettings);
  };

  // Handle auto-scroll change
  const handleAutoScrollChange = (autoScroll) => {
    console.log('🔴 FRONTEND AUTOSCROLL - Auto-scroll toggle changed to:', autoScroll);
    const newSettings = { ...settings, autoScroll };
    console.log('🔴 FRONTEND AUTOSCROLL - Calling saveImmediately with:', newSettings);
    saveImmediately(newSettings);
  };

  // Handle event selection
  const handleEventToggle = (eventId) => {
    if (!eventId) return;

    console.log('🔴 FRONTEND TOGGLE - Event toggle clicked:', eventId);
    const currentIds = settings.selectedEventIds || [];
    console.log('🔴 FRONTEND TOGGLE - Current selectedEventIds:', currentIds);

    const isSelected = currentIds.includes(eventId);
    console.log('🔴 FRONTEND TOGGLE - Is event selected?:', isSelected);

    const selectedEventIds = isSelected
      ? currentIds.filter(id => id !== eventId) // Deselect
      : [...currentIds, eventId]; // Select

    console.log('🔴 FRONTEND TOGGLE - New selectedEventIds:', selectedEventIds);

    const newSettings = { ...settings, selectedEventIds };
    console.log('🔴 FRONTEND TOGGLE - Calling saveImmediately with:', newSettings);
    saveImmediately(newSettings);
  };

  // Select all events
  const handleSelectAllEvents = () => {
    const eventIds = (events || []).map(event => event?.id).filter(Boolean);
    const newSettings = { ...settings, selectedEventIds: eventIds };
    saveImmediately(newSettings);
  };

  // Deselect all events
  const handleDeselectAllEvents = () => {
    const newSettings = { ...settings, selectedEventIds: [] };
    saveImmediately(newSettings);
  };

  // Filter events based on search query
  const filteredEvents = (events || []).filter(event => {
    if (!event || !searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase().trim();
    const eventName = (event.name || '').toLowerCase();
    const eventNumber = (event.itemNumber || '').toString().toLowerCase();
    const eventRound = (event.roundType || '').toLowerCase();

    return eventName.includes(query) ||
           eventNumber.includes(query) ||
           eventRound.includes(query);
  });

  // Highlight search match in text
  const highlightSearchMatch = (text, query) => {
    if (!query || !text) return text;

    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === query.toLowerCase()) {
        return <span key={index} className="search-highlight">{part}</span>;
      }
      return part;
    });
  };

  if (loading) {
    return (
      <div className="callbacks-control-container">
        <div className="loading-message">Loading callbacks settings...</div>
      </div>
    );
  }

  return (
    <div className="callbacks-control-container">
      <div className="callbacks-control-header">
        <h2>Callbacks Display Control</h2>
        <p>Configure how the callbacks screen displays events and rotates between them.</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="callbacks-control-content">
        {/* Enable/Disable */}
        <div className="control-section">
          <h3>Display Settings</h3>
          <div className="toggle-container">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => handleToggleEnabled(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="toggle-label">
              {settings.enabled ? 'Callbacks display enabled' : 'Callbacks display disabled'}
            </span>
          </div>
        </div>

        {/* Auto-scroll Settings */}
        {settings.enabled && (
          <div className="control-section">
            <h3>Auto-scroll Settings</h3>
            <div className="toggle-container">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.autoScroll}
                  onChange={(e) => handleAutoScrollChange(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="toggle-label">
                {settings.autoScroll ? 'Auto-scroll enabled' : 'Auto-scroll disabled'}
              </span>
            </div>

            {settings.autoScroll && (
              <div className="interval-controls">
                <label>
                  Rotate events every:
                  <select
                    value={settings.intervalSeconds}
                    onChange={(e) => handleIntervalChange(parseInt(e.target.value))}
                    className="interval-select"
                  >
                    <option value={5}>5 seconds</option>
                    <option value={10}>10 seconds</option>
                    <option value={15}>15 seconds</option>
                    <option value={30}>30 seconds</option>
                    <option value={60}>1 minute</option>
                    <option value={120}>2 minutes</option>
                    <option value={300}>5 minutes</option>
                  </select>
                </label>
              </div>
            )}
          </div>
        )}

        {/* Event Selection */}
        {settings.enabled && (
          <div className="control-section">
            <h3>Events to Display</h3>
            <div className="events-selection">
              {/* Search Bar */}
              <div className="search-container">
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    placeholder="Search events by number or name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="search-clear-btn"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {searchQuery && (
                  <div className="search-results-info">
                    Found {filteredEvents.length} of {(events || []).length} events
                  </div>
                )}
              </div>
              {(!events || events.length === 0) ? (
                <div className="no-events-message">
                  <p>No events with dancers found.</p>
                  <p>Create events and add dancers to enable callbacks display.</p>
                </div>
              ) : (
                <>
                  <div className="selection-controls">
                    <button
                      type="button"
                      onClick={handleSelectAllEvents}
                      className="select-btn select-all"
                      disabled={searchQuery ? filteredEvents.length === 0 : false}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleDeselectAllEvents}
                      className="select-btn deselect-all"
                      disabled={searchQuery ? filteredEvents.length === 0 : false}
                    >
                      Deselect All
                    </button>
                    <span className="selection-count">
                      {(settings.selectedEventIds || []).length} of {(events || []).length} selected
                      {searchQuery && <span className="filtered-count"> ({filteredEvents.length} filtered)</span>}
                    </span>
                  </div>

                  {searchQuery && filteredEvents.length === 0 ? (
                    <div className="no-search-results">
                      <p>No events found matching "{searchQuery}"</p>
                      <p>Try a different search term or clear the search to see all events.</p>
                    </div>
                  ) : (
                    <div className="events-grid">
                      {filteredEvents.map(event => (
                      <div
                        key={event?.id || Math.random()}
                        className={`event-card ${(settings.selectedEventIds || []).includes(event?.id) ? 'selected' : ''}`}
                        onClick={() => handleEventToggle(event?.id)}
                      >
                        <div className="event-checkbox">
                          <input
                            type="checkbox"
                            checked={(settings.selectedEventIds || []).includes(event?.id)}
                            onChange={() => handleEventToggle(event?.id)}
                            onClick={(e) => e.stopPropagation()} // Prevent card click from interfering
                          />
                        </div>
                        <div className="event-info">
                          <h4 className="event-name">
                            {highlightSearchMatch(event?.name || 'Unknown Event', searchQuery)}
                          </h4>
                          <div className="event-details">
                            <span className="event-number">
                              {event?.itemNumber ? `#${event.itemNumber}` : ''}
                            </span>
                            <span className="event-round">
                              {highlightSearchMatch(event?.roundType || 'TBD', searchQuery)}
                            </span>
                            <span className="event-dancers">
                              {(event?.dancers || []).length || 0} dancers
                            </span>
                            {event?.active && (
                              <span className="event-status active">ACTIVE</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        

        {/* Auto-save indicator and Refresh Button */}
        <div className="control-actions">
          {saving && (
            <div className="saving-indicator">
              Saving...
            </div>
          )}
          <button
            onClick={() => {
              console.log('🔄 Manual refresh requested');
              fetchEvents();
              fetchSettings();
            }}
            className="refresh-btn"
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallbacksControl;